import { parseJsonOrNull } from "replo-utils/lib/json";
import {
  getAgentCoordinatorBaseUrl,
  getAirlockBaseUrl,
  getBedrockApiBaseUrl,
  getPublisherApiBaseUrl,
  getStagingStackName,
  isSharedStagingStack,
  PRODUCTION_AGENT_COORDINATOR_BASE_URL,
  PRODUCTION_AIRLOCK_BASE_URL,
  PRODUCTION_BEDROCK_INTERNAL_API_BASE_URL,
  SHARED_STAGING_STACK_NAME,
} from "replo-utils/lib/stagingStack";

const canUseEnv = typeof process !== "undefined" && Boolean(process.env);

export const isUnitTest = (() => {
  // https://stackoverflow.com/questions/50940640/how-to-determine-if-jest-is-running-the-code-or-not
  if (typeof process !== "undefined" && process.env.JEST_WORKER_ID) {
    return true;
  }
  return false;
})();

export const isDevelopment =
  !isUnitTest && canUseEnv && process.env.NODE_ENV === "development";

const shouldUseLocalHttps =
  typeof process !== "undefined" && process.env.REPLO_LOCAL_HTTPS === "true";
const shouldUseLocalProdData =
  typeof process !== "undefined" &&
  process.env.REPLO_LOCAL_PROD_DATA === "true";

/**
 * Check if the current hostname is an ngrok domain
 */
function isNgrokDomain(hostname: string): boolean {
  return hostname.endsWith(".ngrok.app");
}

/**
 * Mobile dev: `pnpm dev` + ngrok exposes the whole worktree through one
 * tunneled Caddy front door (`https://replo-<alias>.ngrok.app`). Every local
 * service is reachable on that origin under its Caddy path prefix, so remote
 * devices (phones) must dial `window.location.origin` — localhost URLs point
 * at the phone itself.
 */
function isNgrokFrontDoorOrigin(): boolean {
  return (
    typeof window !== "undefined" && isNgrokDomain(window.location.hostname)
  );
}

function getStoredStagingStackName({
  defaultValue,
  key,
}: {
  defaultValue: string;
  key: string;
}): string {
  if (typeof window === "undefined") {
    return defaultValue;
  }

  const parsedValue = parseJsonOrNull(window.localStorage.getItem(key) ?? "");
  if (typeof parsedValue !== "string") {
    return defaultValue;
  }

  try {
    return getStagingStackName({ inputName: key, rawName: parsedValue });
  } catch {
    return defaultValue;
  }
}

function shouldUseBackendStaging(): boolean {
  return (
    typeof window !== "undefined" &&
    window.localStorage.getItem("replo.debug.useBackendStaging") === "true"
  );
}

function isLocalhostHostname(hostname: string): boolean {
  return ["localhost", "127.0.0.1", "::1", "[::1]"].includes(hostname);
}

function getBackendStagingNamespace(): string {
  return getStoredStagingStackName({
    defaultValue: SHARED_STAGING_STACK_NAME,
    key: "replo.debug.stagingNamespace",
  });
}

export function getBackendStagingAliasOverride(): string | null | undefined {
  if (!shouldUseBackendStaging()) {
    return undefined;
  }
  const stackName = getBackendStagingNamespace();
  return isSharedStagingStack(stackName) ? null : stackName;
}

export function getBackendStagingAgentAliasOverride():
  | string
  | null
  | undefined {
  return getBackendStagingAliasOverride();
}

/**
 * The default values we should provide to the `credentials` option of fetch()
 * when we're calling our backend in the editor.
 */
export const defaultFetchCredentials = (() => {
  // Note (Noah, 2025-07-18, REPL-18040): "include" is the default, because we
  // want requests to our backend to respect the Set-Cookie header so that cookies
  // can propagate to subsequent requests. "same-origin" does not work because
  // dashboard.replo.app and publisher.replo.app are different origins.
  //
  // This is fine for CSRF, because our cookies have SameSite=Lax so they'll never
  // be sent by malicious impersonating javascript, and we don't even use cookies
  // for auth anyway (currently).
  return "include" as const;
})();

// NOTE (Patrick, 2026-02-25): Vercel preview deployments have dynamic hostnames
// that can't be whitelisted in Google Cloud Console. We detect these to use
// the Google auth popup proxy.
export function isVercelPreviewDeployment(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return process.env.VERCEL_ENV === "preview";
}

// (Patrick, 2026-03-11): The stable origin for the Google OAuth proxy page.
// This domain is already whitelisted in Google Cloud Console as an authorized
// JavaScript origin. Preview deployments open a popup to this origin to
// complete Google sign-in.
export const GOOGLE_AUTH_PROXY_ORIGIN = "https://dashboard.replo.app";

export function getPublisherUrl() {
  // Explicit Fly override, kept around until the AWS staging cutover is
  // complete. Takes precedence over the default AWS route.
  if (
    typeof window !== "undefined" &&
    window.localStorage.getItem("replo.debug.useBackendStagingFly") === "true"
  ) {
    return "https://replo-publisher-staging.fly.dev";
  }
  if (shouldUseBackendStaging()) {
    return getPublisherApiBaseUrl({ stackName: getBackendStagingNamespace() });
  }

  const hostname = window.location.hostname;

  // Mobile dev: single ngrok tunnel -> Caddy, publisher under /publisher.
  if (isDevelopment && isNgrokFrontDoorOrigin()) {
    return `${window.location.origin}/publisher`;
  }

  // Note (Noah, 2025-07-17): isDevelopment is actually false in e2e tests, so we have
  // to check for localhost. Pretty sure there's a better way to do this
  if (
    !shouldUseLocalProdData &&
    (isDevelopment || isLocalhostHostname(hostname))
  ) {
    const protocol = shouldUseLocalHttps ? "https" : "http";
    // E2E CI runs the publisher directly (no Caddy). In that env, CADDY
    // port is unset and REPLO_DEV_PUBLISHER_PORT points straight at the
    // publisher, which doesn't expect a `/publisher` path prefix.
    const caddyPort = process.env.REPLO_DEV_CADDY_FRONT_DOOR_PORT;
    if (caddyPort) {
      return `${protocol}://${hostname}:${caddyPort}/publisher`;
    }
    // No-Caddy localhost (E2E): the orchestrator points
    // REPLO_DEV_PUBLISHER_PORT straight at the publisher. No hardcoded :3001 —
    // it varies per worktree.
    const publisherPort = process.env.REPLO_DEV_PUBLISHER_PORT;
    return `${protocol}://${hostname}:${publisherPort}`;
  }

  return "https://publisher.replo.app";
}

export function getDashboardUrl() {
  // Note (Noah, 2025-07-17): isDevelopment is actually false in e2e tests, so we have
  // to check for localhost. Pretty sure there's a better way to do this
  if (isDevelopment || isLocalhostHostname(window.location.hostname)) {
    const port = process.env.REPLO_DEV_EDITOR_PORT;
    return `http://localhost:${port}`;
  }
  return "https://dashboard.replo.app";
}

export function getBedrockApiUrl() {
  if (shouldUseBackendStaging()) {
    return getBedrockApiBaseUrl({ stackName: getBackendStagingNamespace() });
  }
  // (Noah, 2026-07-13, REPL-29260): prod-data must win over every local
  // fallback below so all three services resolve to prod together — a split
  // (e.g. local Airlock token + prod coordinator) fails auth with 4001.
  if (shouldUseLocalProdData) {
    return PRODUCTION_BEDROCK_INTERNAL_API_BASE_URL;
  }
  if (isNgrokFrontDoorOrigin()) {
    return `${window.location.origin}/bedrock-api`;
  }
  // Local bedrock-api is only reachable through this worktree's Caddy front
  // door, which the launchers always set. No hardcoded :3001 fallback — when
  // the front-door port is absent there is no local bedrock, so use prod.
  const caddyPort = process.env.REPLO_DEV_CADDY_FRONT_DOOR_PORT;
  if (caddyPort) {
    return `http://localhost:${caddyPort}/bedrock-api`;
  }
  return PRODUCTION_BEDROCK_INTERNAL_API_BASE_URL;
}

export function getCoordinatorApiUrl(): string {
  if (shouldUseBackendStaging()) {
    return getAgentCoordinatorBaseUrl({
      stackName: getBackendStagingNamespace(),
    });
  }
  // (Noah, 2026-07-13, REPL-29260): prod-data must win over the baked
  // AGENT_COORDINATOR_BASE_URL (e.g. the ngrok tunnel) so the coordinator
  // matches the prod Airlock the token is minted against.
  if (shouldUseLocalProdData) {
    return PRODUCTION_AGENT_COORDINATOR_BASE_URL;
  }
  const fromEnv = process.env.AGENT_COORDINATOR_BASE_URL;
  if (fromEnv) {
    return fromEnv;
  }
  // Local coordinator is reachable through this worktree's Caddy front door
  // (same as getBedrockApiUrl), so the editor works without the ngrok tunnel
  // URL being injected into Vite. handle_path strips `/agent-coordinator`.
  const caddyPort = process.env.REPLO_DEV_CADDY_FRONT_DOOR_PORT;
  if (caddyPort) {
    return `http://localhost:${caddyPort}/agent-coordinator`;
  }
  return PRODUCTION_AGENT_COORDINATOR_BASE_URL;
}

export function getAirlockApiUrl(): string {
  if (shouldUseBackendStaging()) {
    return getAirlockBaseUrl({ stackName: getBackendStagingNamespace() });
  }
  // (Noah, 2026-07-13, REPL-29260): mint tokens against prod Airlock so the
  // prod bedrock and coordinator accept them. This must come before the
  // process.env.AIRLOCK_URL / ngrok / Caddy fallbacks: unlike the other URL
  // env vars this one is NOT baked away by Vite, so `pnpm dev`'s local
  // AIRLOCK_URL leaks through at runtime and mints a locally-signed token the
  // prod coordinator rejects with 4001 auth:invalidToken.
  if (shouldUseLocalProdData) {
    return PRODUCTION_AIRLOCK_BASE_URL;
  }
  if (isNgrokFrontDoorOrigin()) {
    return `${window.location.origin}/airlock`;
  }
  if (process.env.AIRLOCK_URL) {
    return process.env.AIRLOCK_URL;
  }
  const caddyPort = process.env.REPLO_DEV_CADDY_FRONT_DOOR_PORT;
  if (caddyPort) {
    return `http://localhost:${caddyPort}/airlock`;
  }
  if (
    typeof window !== "undefined" &&
    isLocalhostHostname(window.location.hostname)
  ) {
    return `http://localhost:${process.env.REPLO_DEV_AIRLOCK_PORT ?? "8789"}`;
  }
  return PRODUCTION_AIRLOCK_BASE_URL;
}

export const SHOPIFY_APP_LISTING_URL = "https://apps.shopify.com/alchemy";
